/**
 * VendorStatusBadge Component
 * Displays vendor status with color coding and icon
 */

import { Badge, BadgeProps } from '@mantine/core';
import {
  IconClock,
  IconPencil,
  IconSend,
  IconEye,
  IconAlertCircle,
  IconCheck,
  IconPlayerPlay,
  IconPlayerPause,
  IconX,
  IconBan,
} from '@tabler/icons-react';

interface VendorStatus {
  statusID: number;
  name: string;
  color?: string;
}

interface VendorStatusBadgeProps extends Omit<BadgeProps, 'children'> {
  status: VendorStatus | number | undefined | null;
  showIcon?: boolean;
}

// Status configuration based on seed data
const STATUS_CONFIG: Record<
  number,
  { label: string; color: string; icon: React.ReactNode }
> = {
  '-5': {
    label: 'Pending',
    color: 'violet',
    icon: <IconClock size={12} />,
  },
  '-4': {
    label: 'Suspended',
    color: 'orange',
    icon: <IconPlayerPause size={12} />,
  },
  '-3': {
    label: 'Deactivated',
    color: 'red',
    icon: <IconX size={12} />,
  },
  '-2': {
    label: 'Rejected',
    color: 'pink',
    icon: <IconBan size={12} />,
  },
  '-1': {
    label: 'More Info Required',
    color: 'orange',
    icon: <IconAlertCircle size={12} />,
  },
  '0': {
    label: 'Draft',
    color: 'gray',
    icon: <IconPencil size={12} />,
  },
  '1': {
    label: 'Submitted',
    color: 'blue',
    icon: <IconSend size={12} />,
  },
  '2': {
    label: 'Under Review',
    color: 'yellow',
    icon: <IconEye size={12} />,
  },
  '3': {
    label: 'Approved',
    color: 'green',
    icon: <IconCheck size={12} />,
  },
  '4': {
    label: 'Active',
    color: 'teal',
    icon: <IconPlayerPlay size={12} />,
  },
};

export function VendorStatusBadge({
  status,
  showIcon = true,
  variant = 'light',
  size = 'sm',
  ...props
}: VendorStatusBadgeProps) {
  // Handle undefined/null status
  if (status === undefined || status === null) {
    return (
      <Badge variant={variant} size={size} color="gray" {...props}>
        Unknown
      </Badge>
    );
  }

  // Handle both object and number status
  const statusID = typeof status === 'number' ? status : status.statusID;
  const statusName = typeof status === 'object' ? status.name : null;

  const config = STATUS_CONFIG[statusID];

  if (!config && !statusName) {
    return (
      <Badge variant={variant} size={size} color="gray" {...props}>
        Unknown
      </Badge>
    );
  }

  const label = statusName || config.label;
  const color = config?.color || 'gray';
  const icon = config?.icon;

  return (
    <Badge
      variant={variant}
      size={size}
      color={color}
      leftSection={showIcon && icon ? icon : undefined}
      {...props}
    >
      {label}
    </Badge>
  );
}
